import React, { useState } from "react";
import { getCorrelation, CorrelationResult, StationCompare } from "../services/api";

const METRICS = ["dpmo", "not_inducted_on_time", "not_dispatched", "oodt", "same_day_miss"];

export function ImpactPage() {
  const [sopId, setSopId] = useState("");
  const [metric, setMetric] = useState("dpmo");
  const [impact, setImpact] = useState<CorrelationResult | null>(null);

  const [stationId, setStationId] = useState("");
  const [stationData, setStationData] = useState<StationCompare | null>(null);

  const loadImpact = async () => {
    if (!sopId) return;
    const data = await getCorrelation("sop_impact", { sop_id: sopId, metric });
    setImpact(data);
  };

  const loadStation = async () => {
    if (!stationId) return;
    const data = await getCorrelation("station_compare", { station_id: stationId });
    setStationData(data);
  };

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: 24 }}>
      <h2>Performance Impact</h2>

      {/* SOP Impact */}
      <section style={{ marginBottom: 40 }}>
        <h3>📈 SOP Impact Analysis</h3>
        <p style={{ color: "#666", fontSize: 14 }}>Compare metrics between stations that adopted a SOP vs. those that didn't.</p>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input value={sopId} onChange={(e) => setSopId(e.target.value)} placeholder="SOP ID" style={{ flex: 2, padding: 8 }} />
          <select value={metric} onChange={(e) => setMetric(e.target.value)} style={{ flex: 1, padding: 8 }}>
            {METRICS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={loadImpact} style={{ padding: "8px 16px" }}>Analyze</button>
        </div>

        {impact && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ padding: 20, background: "#e8f5e9", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#666" }}>Adopters ({impact.adopters.count} stations)</div>
              <div style={{ fontSize: 32, fontWeight: "bold", color: "#2e7d32" }}>
                {impact.adopters.avg ?? "N/A"}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>avg {impact.metric}</div>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                {impact.adopters.stations.map((s) => (
                  <span key={s} style={{ display: "inline-block", background: "#c8e6c9", padding: "2px 8px", borderRadius: 10, margin: 2 }}>{s}</span>
                ))}
              </div>
            </div>
            <div style={{ padding: 20, background: "#fce4ec", borderRadius: 8, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#666" }}>Non-Adopters ({impact.non_adopters.count} stations)</div>
              <div style={{ fontSize: 32, fontWeight: "bold", color: "#c62828" }}>
                {impact.non_adopters.avg ?? "N/A"}
              </div>
              <div style={{ fontSize: 12, color: "#666" }}>avg {impact.metric}</div>
            </div>
          </div>
        )}
      </section>

      {/* Station Timeline */}
      <section>
        <h3>🏭 Station Performance Timeline</h3>
        <p style={{ color: "#666", fontSize: 14 }}>View a station's metrics over time alongside when they adopted SOPs.</p>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <input value={stationId} onChange={(e) => setStationId(e.target.value)} placeholder="Station ID" style={{ flex: 1, padding: 8 }} />
          <button onClick={loadStation} style={{ padding: "8px 16px" }}>Load</button>
        </div>

        {stationData && (
          <>
            {stationData.adoptions.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <strong>SOPs Adopted:</strong>
                {stationData.adoptions.map((a) => (
                  <span key={a.sop_id} style={{ display: "inline-block", background: "#e3f2fd", padding: "2px 10px", borderRadius: 12, margin: "2px 4px", fontSize: 13 }}>
                    {a.sop_id}
                  </span>
                ))}
              </div>
            )}

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #ddd", textAlign: "left" }}>
                  <th style={{ padding: 6 }}>Period</th>
                  {METRICS.map((m) => <th key={m} style={{ padding: 6 }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {stationData.metrics_history.map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={{ padding: 6 }}>{row.period}</td>
                    {METRICS.map((m) => <td key={m} style={{ padding: 6 }}>{row[m] ?? "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {stationData.metrics_history.length === 0 && (
              <p style={{ color: "#999" }}>No metrics data for this station yet.</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
